import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';

import { TicketStatusOption } from '@/constants/ticket-status-option.enum';
import { ticketStatusSelector } from '@/redux/app/selectors/ticket';
import { setTicketStatus } from '@/redux/app/actions/ticket';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';

export interface TicketStatusSelectorProps {
  ticket: string;
  selectorClassName: string;
}

const useClickOutside = (ref: any, callback: any) => {
  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!ref.current.contains(event.target)) callback(false);
    };
    window.document.addEventListener('click', handleClickOutside);

    return () => {
      window.document.removeEventListener('click', handleClickOutside);
    };
  }, [ref, callback]);
};

export const TicketStatusSelector = ({ ticket, selectorClassName }: TicketStatusSelectorProps) => {
  const { subdomain } = useContext(NavigationPaneRouteParamsContext);
  const status = useSelector(_.partial(ticketStatusSelector, ticket));
  const dispatch = useDispatch();
  const [dropdownEnabled, setDropdownEnabled] = useState(false);

  const ticketSelectorRef = React.useRef();
  useClickOutside(ticketSelectorRef, setDropdownEnabled);

  const handleClick = useCallback((event) => {
    const value = event.target.getAttribute('data-status');
    dispatch(setTicketStatus(subdomain, ticket, value));
    setDropdownEnabled(false);
  }, [subdomain, ticket, dispatch, dropdownEnabled]);

  const toggleDropdown = () => {
    setDropdownEnabled(!dropdownEnabled);
  };

  const statusOptionsClassName = dropdownEnabled ? 'select-options' : 'select-options hide';
  const statusOptions = TicketStatusOption.mapValues((value) => (
    <li key={value} role="button" data-status={value} onClick={handleClick}>{value}</li>
  ));

  const selectorId = `status-selector-${ticket}`;

  return (
    <div className={`ticket-status-form ${selectorClassName} noselect`}>
      <div className="select">
        <div
          id={selectorId}
          ref={ticketSelectorRef}
          className="select-styled"
          data-current={status}
          onClick={toggleDropdown}
          role="button"
        >
          <span role="button" className="dot-icon" />
        </div>
        <ul className={statusOptionsClassName} style={{ display: 'block' }}>
          {statusOptions}
        </ul>
      </div>
    </div>
  );
};
